import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Grid,
  Tooltip,
} from "@mui/material";
import Fade from "@mui/material/Fade";

import BookmarksIcon from '@mui/icons-material/Bookmarks';
import AppsIcon from "@mui/icons-material/Apps";
import MicrowaveIcon from "@mui/icons-material/Microwave";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import KeyIcon from '@mui/icons-material/Key';
import SettingsIcon from "@mui/icons-material/Settings";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import BarChartIcon from "@mui/icons-material/BarChart";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import AssessmentIcon from '@mui/icons-material/Assessment';

const ToolsMenu = () => {
  const navigate = useNavigate();
  const [appsAnchorEl, setAppsAnchorEl] = useState(null);

  const openAppsMenu = (event) => setAppsAnchorEl(event.currentTarget);
  const closeAppsMenu = () => setAppsAnchorEl(null);

  const menuItemStyles = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 2,
    borderRadius: 2,
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.08)",
    },
  };

  const iconColors = {
    // A vibrant, professional palette (as chosen previously)
    MicrowaveIcon: { mb: 1, color: "#2196F3" }, // Blue
    BarChartIcon: { mb: 1, color: "#4CAF50" },   // Green
    Diversity3Icon: { mb: 1, color: "#FFC107" }, // Amber
    ListAltIcon: { mb: 1, color: "#9C27B0" },    // Purple
    FollowTheSignsIcon: { mb: 1, color: "#FF5722" }, // Deep Orange
    InsightsIcon: { mb: 1, color: "#00BCD4" },   // Cyan
    KeyIcon: { mb: 1, color: "#ae6c6aff" }, // Pink
    SettingsIcon: { mb: 1, color: "#607D8B" },   // Blue Grey
    HelpOutlineIcon: { mb: 1, color: "#795548" }, // Brown
    AssessmentIcon: { mb: 1, color: "#009688" }, // Teal
    BookmarksIcon: { mb: 1, color: "#2bff00ff" },
  };

  return (
    <>
      <Tooltip title="More Tools" arrow>
        <IconButton
          size="small"
          sx={{
            color: "white",
            p: 1,
            backgroundColor: "rgba(255, 255, 255, 0.08)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.16)",
              transform: "scale(1.1)",
            },
            transition: "all 0.2s ease-in-out",
          }}
          aria-label="Open Tools Menu"
          onClick={openAppsMenu}
        >
          <AppsIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={appsAnchorEl}
        open={Boolean(appsAnchorEl)}
        onClose={closeAppsMenu}
        // Add TransitionComponent for animation
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }} // Adjust timeout for animation duration (in ms)
        PaperProps={{
          sx: {
            mt: 2,
            ml: -2,
            borderRadius: "35px",
            width: 350,
            padding: 3,
            boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.25)",
            background: "rgba(255, 255, 255, 0.89)",
            border: "6px Double rgba(87, 76, 76, 0.13)",
            transition: "all 0.3s ease-in-out",
            "&:hover": {
              boxShadow: "0px 12px 40px rgba(0, 0, 0, 0.35)",
              transform: "translateY(-2px)",
            },
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          },
        }}
      >
        <Grid container spacing={2}>
          {/* First row of tools */}
          <Grid item xs={4}>
            <MenuItem
              onClick={() => {
                navigate("/dashboard/freedatasets");
                closeAppsMenu();
              }}
              sx={menuItemStyles}
            >
              <MicrowaveIcon fontSize="medium" sx={iconColors.MicrowaveIcon} />
              <Typography variant="caption" textAlign="center">
                Free Datasets
              </Typography>
            </MenuItem>
          </Grid>
          <Grid item xs={4}>
            <MenuItem
              onClick={() => {
                navigate("/dashboard/job");
                closeAppsMenu();
              }}
              sx={menuItemStyles}
            >
              <BarChartIcon fontSize="medium" sx={iconColors.BarChartIcon} />
              <Typography variant="caption" textAlign="center">
                Job Analysis
              </Typography>
            </MenuItem>
          </Grid>
          <Grid item xs={4}>
            <MenuItem
              onClick={() => {
                navigate("/dashboard/usersdataset");
                closeAppsMenu();
              }}
              sx={menuItemStyles}
            >
              <Diversity3Icon fontSize="medium" sx={iconColors.Diversity3Icon} />
              <Typography variant="caption" textAlign="center">
                Dataset Assignments
              </Typography>
            </MenuItem>
          </Grid>

          {/* Second row of tools */}
          <Grid item xs={4}>
            <MenuItem
              onClick={() => {
                navigate("/dashboard/exhibitorslist");
                closeAppsMenu();
              }}
              sx={menuItemStyles}
            >
              <ListAltIcon fontSize="medium" sx={iconColors.ListAltIcon} />
              <Typography variant="caption" textAlign="center">
                20% Exhibitors list
              </Typography>
            </MenuItem>
          </Grid>

          <Grid item xs={4}>
            <MenuItem
              onClick={() => {
                navigate("/dashboard/order-list");
                closeAppsMenu();
              }}
              sx={menuItemStyles}
            >
              <ShoppingBasketIcon fontSize="medium" sx={iconColors.FollowTheSignsIcon} />
              <Typography variant="caption" textAlign="center">
                Orders list
              </Typography>
            </MenuItem>
          </Grid>

          <Grid item xs={4}>
            <MenuItem
              onClick={() => {
                navigate("/dashboard/bookmark");
                closeAppsMenu();
              }}
              sx={menuItemStyles}
            >
              <BookmarksIcon fontSize="medium" sx={iconColors.BookmarksIcon} />
              <Typography variant="caption" textAlign="center">
                bookmark
              </Typography>
            </MenuItem>
          </Grid>

          {/* Third row of tools */}
          <Grid item xs={4}>
            <MenuItem
              onClick={() => {
                navigate("/dashboard/apikey");
                closeAppsMenu();
              }}
              sx={menuItemStyles}
            >
              <KeyIcon fontSize="medium" sx={iconColors.KeyIcon} />
              <Typography variant="caption" textAlign="center">
                API key model
              </Typography>
            </MenuItem>
          </Grid>

          <Grid item xs={4}>
            <MenuItem
              onClick={() => {
                navigate("/dashboard/utmrecord");
                closeAppsMenu();
              }}
              sx={menuItemStyles}
            >
              <SettingsIcon fontSize="medium" sx={iconColors.SettingsIcon} />
              <Typography variant="caption" textAlign="center">
                UTM Records
              </Typography>
            </MenuItem>
          </Grid>

          <Grid item xs={4}>
            <MenuItem
              onClick={() => {
                navigate("/dashboard/builder-analytics");
                closeAppsMenu();
              }}
              sx={menuItemStyles}
            >
              <AssessmentIcon fontSize="medium" sx={iconColors.AssessmentIcon} />
              <Typography variant="caption" textAlign="center">
                Builder Analytics
              </Typography>
            </MenuItem>
          </Grid>
        </Grid>
      </Menu>
    </>
  );
};

export default ToolsMenu;